
import { supabase } from "@/integrations/supabase/client";
import { delay } from "./utils/retryUtils";

/**
 * Assigns a guard to a team, using an existing team if it exists or creating a new one if needed
 */
export const assignGuardToTeam = async (guardId: string, teamName: string) => {
  try {
    // First check if a team with this name already exists
    const { data: existingTeams, error: findTeamError } = await supabase
      .from('teams')
      .select('id')
      .ilike('name', teamName);  // Using ilike for case-insensitive matching

    if (findTeamError) {
      console.error('Error finding team:', findTeamError);
      throw new Error(`Failed to find team: ${findTeamError.message}`);
    }

    let teamId: string;

    if (!existingTeams || existingTeams.length === 0) {
      // If no team exists with this name, create a new one
      // First check if the user is an admin
      const { data: isAdmin } = await supabase.rpc('is_admin', {
        user_id: guardId 
      });
      
      if (!isAdmin) {
        // For non-admin users, we'll use a function that has higher privileges
        const { data: newTeam, error: createTeamError } = await supabase.functions.invoke('create-team', {
          body: { teamName }
        });
        
        if (createTeamError || !newTeam) {
          console.error('Error creating team:', createTeamError || 'No team data returned');
          throw new Error(`Failed to create team: ${createTeamError?.message || 'No team data returned'}`);
        }
        
        teamId = newTeam.id;
      } else {
        // Admin users can create teams directly
        const { data: newTeam, error: createTeamError } = await supabase
          .from('teams')
          .insert([{ name: teamName }])
          .select()
          .single();

        if (createTeamError) {
          console.error('Error creating team:', createTeamError);
          throw new Error(`Failed to create team: ${createTeamError.message}`);
        }
        
        teamId = newTeam.id;
        
        // Get current user to assign them to the team in admin_teams
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Assign the creating admin to this team
          const { error: adminTeamError } = await supabase
            .from('admin_teams')
            .insert({
              admin_id: user.id,
              team_id: teamId
            });
          
          if (adminTeamError) {
            console.warn('Could not assign admin to team:', adminTeamError);
            // Don't throw - team was created successfully, just log the warning
          } else {
            console.log('Admin assigned to new team');
          }
        }
      }
      
      console.log('Created new team:', teamName, 'with ID:', teamId);

      // Wait a moment for the database to process the team creation
      await delay(2000);

      // Direct insert into team_members
      console.log('Attempting to add team member:', { team_id: teamId, user_id: guardId });
      const { error: insertError } = await supabase
        .from('team_members')
        .insert({
          team_id: teamId,
          user_id: guardId
        });

      if (insertError) {
        console.error('Error creating team member:', insertError);
        throw new Error(`Warning: Guard created but team assignment failed: ${insertError.message}`);
      }
      
      console.log('Successfully assigned guard to team:', teamName);
    } else {
      // Use the first matching team we found
      teamId = existingTeams[0].id;
      console.log('Found existing team:', teamName, 'with ID:', teamId, 
                `(${existingTeams.length} teams with this name exist)`);

      // Check if the guard is already a member of this team
      const { data: existingMembership, error: checkMembershipError } = await supabase
        .from('team_members')
        .select('id')
        .eq('team_id', teamId)
        .eq('user_id', guardId);
        
      if (checkMembershipError) {
        console.error('Error checking team membership:', checkMembershipError);
      }

      // If not already a member, add them
      if (!existingMembership || existingMembership.length === 0) {
        // Small delay to ensure database consistency
        await delay(1000);

        // Direct insert into team_members
        console.log('Attempting to add team member:', { team_id: teamId, user_id: guardId });
        const { error: insertError } = await supabase
          .from('team_members')
          .insert({
            team_id: teamId,
            user_id: guardId
          });

        if (insertError) {
          console.error('Error creating team member:', insertError);
          throw new Error(`Failed to add team member: ${insertError.message}`);
        }
        
        console.log('Successfully assigned guard to team:', teamName);
      } else {
        console.log('Guard is already a member of team:', teamName);
      }
    }
    
    return { success: true, teamId };
  } catch (error: any) {
    console.error('Error in assignGuardToTeam:', error);
    throw error;
  }
};
